import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Alert,
  FormHelperText,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CityService from '../../../services/city.service';
import ActivityTypeService from '../../../services/activityType.service';
import EventService from '../../../services/event.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

function Form({ open, onClose, initialData = null, onSubmit, onCancel }) {
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    date_start: '',
    date_end: '',
    city_id: '',
    activity_type_id: '',
    event_id: ''
  });

  const [cities, setCities] = React.useState([]);
  const [activityTypes, setActivityTypes] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [generalError, setGeneralError] = React.useState(null);

  React.useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [citiesData, typesData, eventsData] = await Promise.all([
          CityService.list(),
          ActivityTypeService.list(),
          EventService.list()
        ]);
        setCities(citiesData);
        setActivityTypes(typesData);
        setEvents(eventsData);
      } catch (error) {
        console.error('Erreur lors du chargement des données de référence:', error);
      }
    };
    loadReferenceData();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.toISOString().split('.')[0]}`;
      };

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        date_start: formatDateTime(initialData.date_start),
        date_end: formatDateTime(initialData.date_end),
        city_id: initialData.city?.id || '',
        activity_type_id: initialData.activity_type?.id || '',
        event_id: initialData.event?.id || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        date_start: '',
        date_end: '',
        city_id: '',
        activity_type_id: '',
        event_id: ''
      });
    }
    // Réinitialiser les erreurs quand le modal s'ouvre ou que les données changent
    setErrors({});
    setGeneralError(null);
  }, [initialData, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur du champ quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Réinitialiser les erreurs
    setErrors({});
    setGeneralError(null);
    
    // Créer un callback pour recevoir les erreurs
    const errorCallback = (validationErrors) => {
      const errorsObj = {};
      let generalMessage = null;
      
      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach(error => {
          if (error.field) {
            errorsObj[error.field] = error.message;
          } else {
            generalMessage = error.message || 'Une erreur est survenue';
          }
        });
      }
      
      setErrors(errorsObj);
      setGeneralError(generalMessage);
    };
    
    const submittedData = {
      ...formData,
      id: initialData?.id
    };
    
    try {
      if (onSubmit) {
        await onSubmit(submittedData, errorCallback);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      date_start: '',
      date_end: '',
      city_id: '',
      activity_type_id: '',
      event_id: ''
    });
    setErrors({});
    setGeneralError(null);
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier l\'activité' : 'Nouvelle activité'}</span>
        <IconButton
          onClick={handleCancel}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          {generalError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {generalError}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            size="small"
            error={!!errors.description}
            helperText={errors.description}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Date et heure de début"
              name="date_start"
              type="datetime-local"
              value={formData.date_start}
              onChange={handleChange}
              required
              size="small"
              error={!!errors.date_start}
              helperText={errors.date_start}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Date et heure de fin"
              name="date_end"
              type="datetime-local"
              value={formData.date_end}
              onChange={handleChange}
              required
              size="small"
              error={!!errors.date_end}
              helperText={errors.date_end}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <FormControl fullWidth size="small" error={!!errors.city_id}>
            <InputLabel id="city-label">Ville</InputLabel>
            <Select
              labelId="city-label"
              name="city_id"
              value={formData.city_id}
              onChange={handleChange}
              required
              label="Ville"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
            {errors.city_id && <FormHelperText>{errors.city_id}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth size="small" error={!!errors.activity_type_id}>
            <InputLabel id="activity-type-label">Type d'activité</InputLabel>
            <Select
              labelId="activity-type-label"
              name="activity_type_id"
              value={formData.activity_type_id}
              onChange={handleChange}
              required
              label="Type d'activité"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              {activityTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.activity_type_id && <FormHelperText>{errors.activity_type_id}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth size="small" error={!!errors.event_id}>
            <InputLabel id="event-label">Événement</InputLabel>
            <Select
              labelId="event-label"
              name="event_id"
              value={formData.event_id}
              onChange={handleChange}
              required
              label="Événement"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              {events.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name}
                </MenuItem>
              ))}
            </Select>
            {errors.event_id && <FormHelperText>{errors.event_id}</FormHelperText>}
          </FormControl>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button
            onClick={handleCancel}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    date_start: PropTypes.string,
    date_end: PropTypes.string,
    city: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    activity_type: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string  
    }),
    event: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  })
};

export default Form;